/* Updated terminology: ECost -> Exchange Cost */
import React,{useState} from 'react';
import {parseExcelFile} from '../utils/excel';
import {supabase,isSupabaseConfigured} from '../lib/supabase';
import UploadZone from '../components/UploadZone';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiInfo,FiAlertCircle,FiCheckCircle}=FiIcons;
const TABLES={MATERIALS: 'rox_materials_1740635000',ITEMS: 'rox_items_1740635000'};

export default function Config() {
  const [uploadingMats,setUploadingMats]=useState(false);
  const [uploadingItems,setUploadingItems]=useState(false);
  const [error,setError]=useState(null);
  const [lastSuccess,setLastSuccess]=useState(null);
  const configured=isSupabaseConfigured();

  const handleUpload=async (file,table,setUploading)=> {
    if (!configured) {
      setError("Supabase is not configured. Please update your .env file.");
      return;
    } 
    setUploading(true);
    setError(null);
    setLastSuccess(null);
    try {
      const data=await parseExcelFile(file);
      if (!data || data.length===0) {
        throw new Error('File is empty or invalid.');
      } 
      const {error: dbError}=await supabase.from(table).upsert(data,{onConflict: 'item_name'});
      if (dbError) throw dbError;
      setLastSuccess(`Successfully synchronized ${data.length} records to ${table.split('_')[1]}.`);
    } catch (err) {
      console.error('Upload error:',err);
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return ( 
    <div className="space-y-12"> 
      <div className="space-y-4"> 
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">Configuration</h1> 
        <p className="text-xl text-gray-500 font-light"> Upload your Excel files to synchronize the database. Our system now automatically handles variations in column names. </p> 
      </div> 
      {!configured && ( 
        <div className="p-6 bg-amber-50 text-amber-800 rounded-3xl border border-amber-200 flex items-start gap-4"> 
          <SafeIcon icon={FiAlertCircle} className="text-2xl mt-1 flex-shrink-0" /> 
          <div> 
            <h3 className="font-semibold">Supabase Credentials Missing</h3> 
            <p className="text-sm opacity-90 mt-1"> Please update your <code>.env</code> file with valid Supabase credentials to enable database synchronization. </p> 
          </div> 
        </div> 
      )} 
      {error && ( 
        <div className="p-4 bg-red-50 text-red-600 rounded-2xl border border-red-100 flex items-start gap-3"> 
          <SafeIcon icon={FiAlertCircle} className="mt-1 flex-shrink-0" /> 
          <p className="text-sm">{error}</p> 
        </div> 
      )} 
      {lastSuccess && ( 
        <div className="p-4 bg-green-50 text-green-600 rounded-2xl border border-green-100 flex items-start gap-3 animate-in fade-in slide-in-from-top-2"> 
          <SafeIcon icon={FiCheckCircle} className="mt-1 flex-shrink-0" /> 
          <p className="text-sm font-medium">{lastSuccess}</p> 
        </div> 
      )} 
      <div className="grid md:grid-cols-2 gap-6"> 
        <UploadZone title="Materials Update" description="Excel headers like 'Name' or 'Item Name' are accepted." isUploading={uploadingMats} onUpload={(file)=> handleUpload(file,TABLES.MATERIALS,setUploadingMats)} /> 
        <UploadZone title="Items Update" description="Sync your recipe items. 'item_name' is the unique identifier." isUploading={uploadingItems} onUpload={(file)=> handleUpload(file,TABLES.ITEMS,setUploadingItems)} /> 
      </div> 
      <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm"> 
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"> 
          <SafeIcon icon={FiInfo} className="text-blue-500" /> Supported Column Names 
        </h3> 
        <div className="grid md:grid-cols-2 gap-8 text-sm text-gray-600"> 
          <div className="space-y-2"> 
            <p className="font-medium text-gray-900 border-b pb-1">Materials Table</p> 
            <ul className="list-disc ml-4 space-y-1"> 
              <li>ItemName / Name / Item Name</li> 
              <li>Category</li> 
              <li>SubCategory1 / Sub Category 1</li> 
              <li>StaminaCost / Stamina Cost</li> 
              <li>Level / Location</li> 
              <li>ReqMaterial / ReqMatCost</li> 
              <li>ECost / Exchange Cost / E Cost</li> 
            </ul> 
          </div> 
          <div className="space-y-2"> 
            <p className="font-medium text-gray-900 border-b pb-1">Items Table</p> 
            <ul className="list-disc ml-4 space-y-1"> 
              <li>ItemName / Name</li> 
              <li>Category / SubCategory</li> 
              <li>Material1 / Qty1</li> 
              <li>Material2 / Qty2</li> 
              <li>ECost / Exchange Cost / E Cost</li> 
            </ul> 
          </div> 
        </div> 
      </div> 
    </div> 
  );
}